import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { Category, SubCategory } from "../util/ExampleData";
import { getAllCategories, getSubCategories } from "../api/CategoryApiCall";
import { KowledgeSummary } from "../api/KnowledgeApiCall";
import { showConsoleError } from "../util/ConsoleMessage";

interface CategoriesState {
  categories: Category[];
  subCategories: SubCategory[];
  totalKnowledgeCount: number;
  loading: boolean;
  error?: string;
}

const initialCategoriesState: CategoriesState = {
  categories: [],
  subCategories: [],
  totalKnowledgeCount: 0,
  loading: false,
};

export const fetchCategories = createAsyncThunk(
  "categories/fetchCategories",
  async (_, { rejectWithValue }) => {
    try {
      const [cats, subs] = await Promise.all([
        getAllCategories(),
        getSubCategories(),
      ]);
      return { cats: cats ?? [], subs: subs ?? [] };
    } catch (e: any) {
      showConsoleError("fetchCategories error", e);
      return rejectWithValue(String(e));
    }
  }
);

export const fetchKnowledgeSummary = createAsyncThunk(
  "categories/fetchKnowledgeSummary",
  async (_, { rejectWithValue }) => {
    try {
      const res = await KowledgeSummary();
      const total =
        res?.categories?.reduce(
          (sum: number, c: any) => sum + c.knowledge_count,
          0
        ) ?? 0;
      return total as number;
    } catch (e: any) {
      showConsoleError("fetchKnowledgeSummary error", e);
      return rejectWithValue(String(e));
    }
  }
);

export const categoriesSlice = createSlice({
  name: "categories",
  initialState: initialCategoriesState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCategories.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.categories = action.payload.cats;
        state.subCategories = action.payload.subs;
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchKnowledgeSummary.fulfilled, (state, action) => {
        state.totalKnowledgeCount = action.payload;
      });
  },
});

export default categoriesSlice.reducer;
